import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Rocket, RefreshCcw, AlertTriangle, CheckCircle2, ListChecks } from "lucide-react";
import { apiGet } from "@/lib/api";
import { toast } from "sonner";

/** Auto-detected pre-launch readiness checklist (admin only). */
const LaunchChecklistCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const d = await apiGet("/admin/launch-checklist");
      setData(d);
    } catch (e) {
      toast.error("Could not load launch checklist");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const summary = data?.summary;
  const ready = !!summary?.ready_for_soft_launch;
  const okPct = summary ? Math.round((summary.ok / summary.total) * 100) : 0;

  const toneFor = (s) =>
    s === "ok"
      ? "text-emerald-600 dark:text-emerald-400"
      : s === "warn"
      ? "text-[hsl(var(--warning))]"
      : "text-destructive";

  return (
    <Card data-testid="launch-checklist-card" className="border">
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2 font-display text-base">
            <Rocket className="h-4 w-4 text-primary" />
            Pre-launch checklist
            {ready ? (
              <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/40">
                <CheckCircle2 className="h-3 w-3 mr-1" /> Ready for soft launch
              </Badge>
            ) : (
              <Badge variant="outline" className="border-destructive/40 text-destructive">
                <AlertTriangle className="h-3 w-3 mr-1" /> {summary?.blocker || "?"} blocker(s)
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            Auto-detected status of every must-do item before sending the link to real users.
          </CardDescription>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="launch-checklist-refresh"
        >
          <RefreshCcw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {summary && (
          <div className="space-y-2">
            <div className="flex items-center gap-3 text-sm">
              <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
                {summary.ok} ready
              </span>
              {summary.warn > 0 && (
                <span className="text-[hsl(var(--warning))] font-semibold">
                  {summary.warn} warning{summary.warn === 1 ? "" : "s"}
                </span>
              )}
              {summary.blocker > 0 && (
                <span className="text-destructive font-semibold">
                  {summary.blocker} blocker{summary.blocker === 1 ? "" : "s"}
                </span>
              )}
              <span className="ml-auto text-muted-foreground">{summary.ok}/{summary.total}</span>
            </div>
            <Progress value={okPct} data-testid="launch-checklist-progress" />
          </div>
        )}
        <ul className="space-y-1.5" data-testid="launch-checklist-items">
          {(data?.items || []).map((it, i) => (
            <li
              key={i}
              className="flex items-start gap-2 text-sm"
              data-testid={`launch-checklist-item-${it.status}`}
            >
              <span className="text-base leading-tight">{it.icon}</span>
              <div className="flex-1 min-w-0">
                <div className={`font-medium ${toneFor(it.status)}`}>{it.label}</div>
                {it.detail && (
                  <div className="text-xs text-muted-foreground truncate">{it.detail}</div>
                )}
              </div>
            </li>
          ))}
        </ul>
        {!loading && (data?.items || []).length === 0 && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <ListChecks className="h-4 w-4" /> Nothing to show yet.
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LaunchChecklistCard;
